import React, {PureComponent, Fragment, Suspense} from 'react'
import PropTypes from 'prop-types'
import {withRouter} from 'react-router-dom'
import {compose} from 'redux'
import {connect} from 'react-redux'
import {createStructuredSelector} from 'reselect'
import {Link, Route} from 'react-router-dom'
import {providePopup} from 'rambler-ui'
import MD5 from 'md5.js'
import {retry} from '@rambler-id/promise'
import {OnWidth} from '@rambler-id/react-utils'
import * as localStorage from '@rambler-id/local-storage'
import {getWebAuthnAvailability} from '@rambler-id/webauthn'
import {
  GET_EMAIL_ACCOUNT_INFO,
  LOG_ACCOUNT_ATTACHMENT_REFUSE
} from '@rambler-id/transport'
import {apiV3} from 'utils/transport'
import internal from 'utils/internal'
import redirect from 'utils/redirect'
import testSha1 from 'utils/testSha1'
import injectReducer from 'utils/injectReducer'
import injectExperiment from 'utils/injectExperiment'
import {
  PROFILE,
  DELETE_PROFILE,
  ADD_PHONE_ACCOUNT,
  ADD_EMAIL_ACCOUNT,
  CHANGE_PASSWORD,
  CHANGE_QUESTION,
  CHANGE_PHONE
} from 'utils/urls'
import InternalPage from 'components/InternalPage'
import ProfileHeader from 'components/ProfileHeader'
import {ProfileNavigation} from 'components/ProfileNavigation'
import SideNavigation from 'components/SideNavigation'
import MainContent from 'components/MainContent'
import HeadProfiles from 'components/HeadProfiles'
import {
  selectProfileType,
  selectDisplay,
  selectInfo,
  selectProfile,
  selectBlockedRamblerMail,
  selectEmails,
  selectPhones,
  selectChampionat,
  selectDefaultChampionat,
  selectSocialProfiles,
  selectUnconfirmedEmail,
  selectWebAuthnKeys,
  selectEmailCredentials,
  selectSessionCredentials
} from 'models/user/selectors'
import {withVerifyingData} from 'models/verifyingData'
import {SuspensePopup} from 'components/SuspensePopup'
import Side from './Side'
import UserCard from './UserCard'
import Suggestions from './Suggestions'
import General from './General'
import Settings from './Settings'
import WebAuthn from './WebAuthn'
import Emails from './Emails'
import Phones from './Phones'
import Social from './Social'
import DefaultChampionat from './DefaultChampionat'
import UnconfirmedEmail from './UnconfirmedEmail'
import reducer from './reducer'
import styles from './styles.css'
import {
  setEditable,
  changeProfile,
  attachSocial,
  detachSocial,
  deleteContact,
  setDefaultAccount,
  resendEmailOrClosePopup,
  resendEmailForContact,
  throwInlineError
} from './actions'
import {
  selectEditable,
  selectInlineError,
  selectContactEmails,
  selectContactPhones,
  selectSentEmails,
  selectSuggestTfa,
  selectSuggestQuestion,
  selectSuggestPhone
} from './selectors'
import {SuspenseFallback} from 'components/SuspenseFallback'

const ChangePassword = React.lazy(
  retry(
    () =>
      import(
        /* webpackChunkName: "change-password" */ 'containers/ChangePassword'
      )
  )
)
const ChangePhone = React.lazy(
  retry(
    () =>
      import(/* webpackChunkName: "change-phone" */ 'containers/ChangePhone')
  )
)
const ChangeQuestion = React.lazy(
  retry(
    () =>
      import(
        /* webpackChunkName: "change-question" */ 'containers/ChangeQuestion'
      )
  )
)
const LinkProfiles = React.lazy(
  retry(
    () =>
      import(/* webpackChunkName: "link-profiles" */ 'containers/LinkProfiles')
  )
)
const AddPhoneAccount = React.lazy(
  retry(
    () =>
      import(
        /* webpackChunkName: "add-phone-account" */ 'containers/AddPhoneAccount'
      )
  )
)
const AddEmailAccount = React.lazy(
  retry(
    () =>
      import(
        /* webpackChunkName: "add-email-account" */ 'containers/AddEmailAccount'
      )
  )
)

class Profile extends PureComponent {
  static propTypes = {
    editable: PropTypes.string,
    inlineError: PropTypes.string,
    openPopup: PropTypes.func.isRequired,
    setEditable: PropTypes.func.isRequired
  }

  webAuthnAvailable = getWebAuthnAvailability({withUserVerifying: true})

  state = {
    suggestTfa: this.props.suggestTfa
  }

  async componentDidMount() {
    this.checkLinkProfiles()
    this.checkChampDefault()
    this.checkUnconfirmedEmail()
  }

  componentDidUpdate(prevProps) {
    const {
      inlineError,
      cancelInlineError,
      emailCredentials,
      suggestTfa,
      type,
      firstVerifyingCredential,
      suggestQuestion,
      suggestPhone,
      sessionCredentials: {time, account},
      location
    } = this.props

    if (inlineError && inlineError !== prevProps.inlineError) {
      this.inlineErrorTimeout = setTimeout(() => {
        cancelInlineError()
      }, 5000)
    }

    if (emailCredentials !== null && prevProps.suggestTfa !== suggestTfa) {
      this.setState({suggestTfa})
    }

    if (
      firstVerifyingCredential &&
      !prevProps.firstVerifyingCredential &&
      type === 'general'
    ) {
      const recentlyLogged = Date.now() - time * 1000 < 1e3 * 60

      if (recentlyLogged && location.pathname === PROFILE) {
        if (suggestQuestion) {
          redirect(`${CHANGE_QUESTION}?promo=1`)

          return
        }

        const loginHistory = localStorage.getItem('loginHistory') || {}
        const hashLogin = new MD5().update(account).digest('hex')
        const currentLogin = loginHistory[hashLogin]

        if (
          currentLogin &&
          (currentLogin.counter + 1) % 5 === 0 &&
          suggestPhone
        ) {
          redirect(`${CHANGE_PHONE}?promo=1`)
        }
      }
    }
  }

  componentWillUnmount() {
    clearTimeout(this.inlineErrorTimeout)
  }

  async checkLinkProfiles() {
    const {info, emails, type, openPopup} = this.props

    if (type !== 'external' || info.email == null) {
      return
    }

    const linkRefuse =
      type === 'external'
        ? Math.round(Date.now() / 1e3) -
            parseInt(info.lastAccountAttachmentRefuse, 10) <
          15778463
        : null

    const accountNotLinked =
      !!type &&
      emails.filter(({accountId}) => accountId === info.email).length === 0

    if (linkRefuse || !accountNotLinked) {
      return
    }

    const emailInfo = await apiV3(`/legacy/${GET_EMAIL_ACCOUNT_INFO}`, {
      email: info.email
    })

    if (emailInfo.type === 'rambler' || emailInfo.exists > 0) {
      try {
        await openPopup((onResolve, onReject) => (
          <SuspensePopup fallback={<div />}>
            {(props) => (
              <LinkProfiles
                onResolve={onResolve}
                onReject={onReject}
                {...props}
              />
            )}
          </SuspensePopup>
        )).closed
      } catch (error) {
        if (this.props.type === 'external') {
          apiV3(`/legacy/${LOG_ACCOUNT_ATTACHMENT_REFUSE}`)
        }
      }
    }
  }

  async checkChampDefault() {
    const {
      type,
      defaultChampionat,
      championat,
      location,
      setDefaultAccount,
      openPopup
    } = this.props

    if (championat.length === 0) {
      return
    }

    const newChamp =
      !!location.pathname.match('/profile') &&
      !!location.search.match('code=success')

    const voluntaryChoise =
      !!location.pathname.match('/default-champ') ||
      (newChamp && championat.length === 2)

    const defaultNotExists = type && defaultChampionat === null

    if (voluntaryChoise || defaultNotExists) {
      try {
        const accountId = await openPopup((onResolve) => (
          <SuspensePopup fallback={<div />}>
            {(props) => (
              <DefaultChampionat
                accounts={championat}
                defaultAccount={defaultChampionat}
                closable={voluntaryChoise}
                onResolve={onResolve}
                {...props}
              />
            )}
          </SuspensePopup>
        )).closed

        setDefaultAccount(accountId)
      } catch (error) {} // eslint-disable-line no-empty
    }

    if (newChamp || voluntaryChoise) {
      redirect(PROFILE)
    }
  }

  async checkUnconfirmedEmail() {
    const {type, unconfirmedEmail, resendEmailOrClosePopup, openPopup} =
      this.props

    if (type === 'service' && unconfirmedEmail) {
      try {
        await openPopup((onResolve) => (
          <SuspensePopup fallback={<div />}>
            {(props) => (
              <UnconfirmedEmail
                onResolve={async () => resendEmailOrClosePopup(onResolve)}
                unconfirmedEmail={unconfirmedEmail}
                {...props}
              />
            )}
          </SuspensePopup>
        )).closed
      } catch (error) {} // eslint-disable-line no-empty
    }
  }

  render() {
    const {suggestTfa} = this.state
    const {webAuthnKeys} = this.props

    const webAuthnAvailableOrHasKeys =
      this.webAuthnAvailable || (webAuthnKeys && webAuthnKeys.length > 0)

    return (
      <Fragment>
        <Suspense fallback={<SuspenseFallback fullPageProgress />}>
          <Route
            exact
            path={ADD_PHONE_ACCOUNT}
            render={(props) => <AddPhoneAccount {...this.props} {...props} />}
          />
          <Route
            exact
            path={ADD_EMAIL_ACCOUNT}
            render={(props) => <AddEmailAccount {...props} />}
          />
          <Route
            exact
            path={CHANGE_PASSWORD}
            render={(props) => <ChangePassword {...props} />}
          />
          <Route
            exact
            path={CHANGE_QUESTION}
            render={(props) => <ChangeQuestion {...props} />}
          />
          <Route
            exact
            path={CHANGE_PHONE}
            render={(props) => <ChangePhone {...props} />}
          />
        </Suspense>
        <Route
          render={() => (
            <InternalPage
              title="Профиль"
              header={
                <ProfileHeader className={styles.header}>
                  <UserCard {...this.props}>
                    <HeadProfiles {...this.props} />
                  </UserCard>
                </ProfileHeader>
              }>
              <OnWidth>
                {(width) =>
                  width >= 768 && (
                    <SideNavigation>
                      <ProfileNavigation
                        {...this.props}
                        webAuthnAvailableOrHasKeys={webAuthnAvailableOrHasKeys}
                        cerberId={(target) => `profile::navigation::${target}`}
                      />
                    </SideNavigation>
                  )
                }
              </OnWidth>
              <MainContent className={styles.content}>
                <Suggestions
                  {...this.props}
                  suggestTfa={suggestTfa}
                  webAuthnAvailable={this.webAuthnAvailable}
                />
                <General {...this.props} />
                <Settings {...this.props} />
                {webAuthnAvailableOrHasKeys && (
                  <WebAuthn
                    {...this.props}
                    webAuthnAvailable={this.webAuthnAvailable}
                  />
                )}
                <Emails {...this.props} />
                <Phones {...this.props} />
                <Social {...this.props} />
                <OnWidth>{(width) => width < 1024 && <Side />}</OnWidth>
                <footer className={styles.footer}>
                  <p>
                    <Link to={DELETE_PROFILE}>
                      Удалить мой профиль и все данные
                    </Link>
                  </p>
                </footer>
              </MainContent>
              <OnWidth>{(width) => width >= 1024 && <Side />}</OnWidth>
            </InternalPage>
          )}
        />
      </Fragment>
    )
  }
}

const mapStateToProps = createStructuredSelector({
  type: selectProfileType,
  editable: selectEditable,
  inlineError: selectInlineError,
  display: selectDisplay,
  info: selectInfo,
  profile: selectProfile,
  unconfirmedEmail: selectUnconfirmedEmail,
  emails: selectEmails,
  blockedRamblerMail: selectBlockedRamblerMail,
  phones: selectPhones,
  contactEmails: selectContactEmails,
  contactPhones: selectContactPhones,
  sentEmails: selectSentEmails,
  championat: selectChampionat,
  defaultChampionat: selectDefaultChampionat,
  socialProfiles: selectSocialProfiles,
  webAuthnKeys: selectWebAuthnKeys,
  emailCredentials: selectEmailCredentials,
  suggestTfa: selectSuggestTfa,
  suggestQuestion: selectSuggestQuestion,
  suggestPhone: selectSuggestPhone,
  sessionCredentials: selectSessionCredentials
})

function mapDispatchToProps(dispatch) {
  return {
    setEditable: (field) => dispatch(setEditable(field)),
    cancelInlineError: () => dispatch(throwInlineError(null)),
    changeProfile: (profile) => dispatch(changeProfile(profile)),
    attachSocial: (event = {}) => dispatch(attachSocial(event)),
    detachSocial: (id, provider) => dispatch(detachSocial(id, provider)),
    deleteContact: (contact) => dispatch(deleteContact(contact)),
    setDefaultAccount: (accountId) => dispatch(setDefaultAccount(accountId)),
    resendEmailOrClosePopup: (emailsInfo, onResolve) =>
      dispatch(resendEmailOrClosePopup(emailsInfo, onResolve)),
    resendEmailForContact: (contact) => dispatch(resendEmailForContact(contact))
  }
}

const withConnect = connect(mapStateToProps, mapDispatchToProps)
const withReducer = injectReducer({key: 'profile', reducer})
const withExperiment = injectExperiment('shatest', testSha1)

export default compose(
  providePopup,
  withExperiment,
  withReducer,
  withVerifyingData(),
  withConnect,
  withRouter,
  internal
)(Profile)
